import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_payairo/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {clearAuthSession} from './authTokenService'
import {WithChildren} from '../../../../_payairo/helpers'
import {AUTH_LOCAL_STORAGE_KEY} from '../../../utils'
import {getAdminMe, mapAdminMeToProfileData} from './_requests'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  checkPermission: (page: string, permission: string) => boolean
  hasPermission: (permissionCode: string) => boolean
  hasAnyPermission: (permissionCodes: string[]) => boolean
  hasAllPermissions: (permissionCodes: string[]) => boolean
}

export type PermissionGuardProps = WithChildren & {
  permission?: string
  permissions?: string[]
  requireAll?: boolean
  fallback?: React.ReactNode
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  checkPermission: (page: string, permission: string) => {
    return false
  },
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }
  // Legacy checkPermission for backwards compatibility if needed, but it should just wrap hasPermission if possible.
  // We'll keep its signature but map it to something safe or just return false since old group_obj doesn't exist.
  const checkPermission = (page: string, permission: string) => {
    return false // Deprecated: use hasPermission instead
  }

  const getPowers = (): string[] => {
    if (!currentUser) return []
    // SUPER_ADMIN check is optional if backend returns all powers for them anyway,
    // but we can rely on effective_powers which is populated by mapAdminMeToProfileData.
    if (currentUser.effective_powers && Array.isArray(currentUser.effective_powers)) {
      return currentUser.effective_powers
    }
    if (currentUser.powers && Array.isArray(currentUser.powers)) {
      return currentUser.powers
    }
    return []
  }

  const isSuperAdmin = () => {
    if (!currentUser) return false
    
    const normalize = (val: any) => String(val || '').toLowerCase().replace(/_|\s/g, '')
    const role = normalize(currentUser.role)
    const userType = normalize(currentUser.user_type)

    return (
      currentUser.is_super_admin === true ||
      String(currentUser.is_super_admin).toLowerCase() === 'true' ||
      role === 'superadmin' ||
      userType === 'superadmin'
    )
  }

  const hasPermission = (permissionCode: string) => {
    if (isSuperAdmin()) return true
    return getPowers().includes(permissionCode)
  }

  const hasAnyPermission = (permissionCodes: string[]) => {
    if (isSuperAdmin()) return true
    if (!permissionCodes || permissionCodes.length === 0) return true
    const powers = getPowers()
    return permissionCodes.some((code) => powers.includes(code))
  }

  const hasAllPermissions = (permissionCodes: string[]) => {
    if (isSuperAdmin()) return true
    if (!permissionCodes || permissionCodes.length === 0) return true
    const powers = getPowers()
    return permissionCodes.every((code) => powers.includes(code))
  }

  const logout = () => {
    clearAuthSession()
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        checkPermission,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, currentUser, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          // First set user from local storage to avoid layout shifting
          if (localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)) {
            const mData = JSON.parse(localStorage.getItem(AUTH_LOCAL_STORAGE_KEY) || '')
            const userData = mData?.user || mData
            const userRole = userData?.role || mData?.role
            const userType = userData?.user_type || mData?.user_type

            setCurrentUser({
              ...userData,
              role: userRole,
              user_type: userType,
              profileData: userData?.profileData || mData?.profileData,
            })
          }

          // Then fetch the latest correct roles from the API
          try {
            const payload = await getAdminMe()
            const profileData = mapAdminMeToProfileData(payload)
            
            setCurrentUser((prev: any) => ({
              ...prev,
              ...profileData,
              role: profileData.role || prev?.role || 'Admin',
              user_type: profileData.user_type || prev?.user_type || 'Admin',
            }))
          } catch (apiErr) {
            console.error('Failed to fetch user profile on load', apiErr)
          }
        }
      } catch (error) {
        // console.error(error)
        // if (!didRequest.current) {
        //   logout()
        // }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.access_token) {
      requestUser(auth.access_token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

const PermissionGuard: FC<PermissionGuardProps> = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const {hasPermission, hasAnyPermission, hasAllPermissions} = useAuth()
  let isAllowed = true

  if (permission) {
    isAllowed = hasPermission(permission)
  } else if (permissions && permissions.length > 0) {
    isAllowed = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions)
  }

  return <>{isAllowed ? children : fallback}</>
}

export {AuthProvider, AuthInit, useAuth, PermissionGuard}
